import { Link } from '@tanstack/react-router';
import { ChevronDown, Plus, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { newMenu, type NewMenuItem } from '../navigation.ts';
import { useScreen } from '../lib/layout.ts';
import { cn } from '../lib/utils.ts';
import { AskTillButton } from './ask-till.tsx';
import { ClockChip } from './clock-chip.tsx';
import { SearchPill } from './global-search.tsx';
import { NotificationBell } from './notification-bell.tsx';
import { Popover } from './ui/popover.tsx';
import { SystemStatusButton } from './system-status.tsx';

/**
 * Liste der Anlege-Ziele (EXPERIENCE §2.4) aus `newMenu` – eine Quelle für das Popover „+ Neu“ (Desktop, Tablet) und den
 * Eintrag „Neu anlegen“ in der Menü-Schublade am Handy (Story 1.114). Ziele, die es noch nicht gibt, stehen grau mit
 * „kommt bald“.
 */
export function NewMenuList({ onNavigate, large, items = newMenu }: { onNavigate: () => void; large?: boolean; items?: readonly NewMenuItem[] }) {
  // In der Schublade (nur Handy) immer 44 px hoch wie ihre übrigen Einträge; im Popover 40 px, bei Touch 44 px.
  const height = large ? 'h-11' : 'h-10 pointer-coarse:h-11';
  return (
    <ul aria-label="Neu anlegen" className="flex list-none flex-col p-0 pointer-coarse:gap-2">
      {items.map((item) => (
        <li key={item.label}>
          {item.path ? (
            <Link to={item.path} search={item.search} onClick={onNavigate} className={cn('flex items-center gap-2 rounded-field px-3 text-row text-ink hover:bg-tile', height)}>
              {item.label}
              {/* KI-Ziel („Bestellung einlesen“) mit Funke ✦ – seit Ticket 1.275 nur noch hier, nicht als Chip in der Kopfzeile. */}
              {item.chip?.ai ? <Sparkles size={14} strokeWidth={1.75} aria-hidden className="flex-none text-[var(--brand-orange)]" /> : null}
            </Link>
          ) : (
            <span aria-disabled="true" className={cn('flex items-center justify-between gap-3 rounded-field px-3 text-row text-ink-3', height)}>
              {item.label}
              <span className="type-meta">kommt bald</span>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * „+ Neu“ (EXPERIENCE §2.4): öffnet alle Anlege-Ziele aus `newMenu` (`NewMenuList`). Seit Ticket 1.275 [RUDOLF 25.10.2026]
 * ein ruhiger Chip „+ Neu ▾“ mit feinem Rand (44 px, Pille), damit der einzige dunkelblaue Knopf einer Seite ihr eigener
 * Hauptknopf bleibt (EK8) – am PC und am Tablet; unter 1024 px nur „+“ (Name bleibt „Neu“).
 * Am Handy steht „Neu anlegen“ seit Story 1.114 in der Menü-Schublade statt in der Kopfzeile. `items`: Ziele der Rolle
 * (Story 23.17: Mitarbeiter nur „Aufgabe“).
 */
export function NewMenu({ items = newMenu }: { items?: readonly NewMenuItem[] } = {}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      label="Neu anlegen"
      align="start"
      className="max-h-[70vh] w-[min(260px,calc(100vw-32px))] overflow-y-auto p-2"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          aria-label="Neu"
          data-testid="neu-menue"
          className={cn(
            'inline-flex h-10 min-w-10 flex-none cursor-pointer items-center justify-center gap-space-1 rounded-full border border-line px-space-3 text-row font-medium whitespace-nowrap text-ink transition-colors duration-150 hover:bg-hover pointer-coarse:h-11',
          )}
        >
          <Plus size={18} strokeWidth={1.75} aria-hidden />
          <span aria-hidden className="max-lg:hidden">
            Neu
          </span>
          <ChevronDown size={14} strokeWidth={1.75} aria-hidden className="flex-none text-ink-3 max-lg:hidden" />
        </button>
      )}
    >
      <NewMenuList onNavigate={() => setOpen(false)} items={items} />
    </Popover>
  );
}

/**
 * Anlegen in der Kopfzeile am PC (Ticket 1.233; seit Ticket 1.275 [RUDOLF 25.10.2026] nur noch der ruhige Chip „+ Neu ▾“):
 * „Auftrag“, „Angebot“ und „✦ Bestellung einlesen“ stehen nicht mehr als eigene Chips in der Kopfzeile, sondern im Menü
 * (Ziele aus `newMenu`; PDF auf jede Seite ziehen bleibt). An die frei gewordene Stelle kommt nichts.
 */
export function CreateChips() {
  return (
    <nav aria-label="Neu anlegen" data-testid="anlege-chips" className="flex flex-none items-center gap-space-2">
      <NewMenu />
    </nav>
  );
}

/**
 * Kopfzeile Design S3 (Change 11.10.2026, DESIGN „Design S3 › Kopfzeile“, EXPERIENCE §2.4) für Desktop und Tablet, oben
 * im Inhalt jeder Seite: links die breite globale Suche (rein, nie mit KI-Eingabe vermischt; Strg K), daneben das
 * Anlegen, rechts die Glocke. Am Handy gilt weiter die feste Kopfzeile Handy der App-Hülle.
 *
 * Anlegen (Ticket 1.233, seit 1.275 ruhig): am PC (ab 1280 px) nur der Chip „+ Neu ▾“ (`CreateChips`; „✦ Bestellung
 * einlesen“ steht im Menü, [RUDOLF 25.10.2026]), darunter (Tablet) derselbe ruhige Chip (`NewMenu`); beide mit allen Zielen aus `newMenu`, fehlende
 * grau „kommt bald“. Danach der Stempel-Chip (Ticket 1.275, `ClockChip`). Rechts der Knopf „KI-Copilot“ (Leiste „KI-Copilot“,
 * Story 19.11) vor der Systemstatus (Story 1.98) und Glocke. Hell/Dunkel sitzt im Profil-Menü.
 */
export function S3Header() {
  const screen = useScreen();
  return (
    <header data-testid="kopfzeile" className="mb-[var(--page-head-gap)] flex min-w-0 items-center gap-2">
      <SearchPill />
      {screen === 'desktop' ? <CreateChips /> : <NewMenu />}
      {/* Ticket 1.275 [RUDOLF 25.10.2026]: Stempel-Chip zwischen „+ Neu ▾“ und KI-Copilot (nur mit Mitarbeiter-Stammsatz). */}
      <ClockChip />
      <AskTillButton />
      <SystemStatusButton />
      <NotificationBell />
    </header>
  );
}
